import React, { memo, useState, useCallback, useRef, RefObject, useEffect, useMemo } from "react";
import { ReactElement } from "react";
import {
    BottomSheet,
    BottomSheetChildrenProps,
    useFocusDialog,
    getActiveElement,
    AriaHidden,
    useFocusTrap,
} from "@swiggy-private/core-ui";
import { AddressInfo } from "@swiggy-private/cart-types";
import { Address } from "./components/Address";
import { SELECT_ADDRESS_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { ChevronRightIcon } from "@cart-ui/components/address-widget-top/components/icons";

import styles from "./styles/AddressList.scss";
import { ADDRESS_LIST_SOURCE } from "@cart-ui/constants/Address";

export interface AddressListProps {
    addresses: AddressInfo[];
    addNewAddress: ({
        selectedAddress,
        addressList,
        source,
    }: {
        selectedAddress?: AddressInfo | null;
        addressList?: AddressInfo[];
        source?: string;
    }) => void;
    selectAddress: (selectedAddress: AddressInfo, addressList: AddressInfo[]) => void;
    hideOnOverlayClick?: boolean;
    onClose?: VoidFunction;
    onImpression: (selectedAddress: AddressInfo | null, addressList: AddressInfo[]) => void;
    selectedAddressIndex: number;
}

type AddressListContentProps = Omit<AddressListProps, "hideOnOverlayClick" | "onClose">;

const TEXTS = {
    CHOOSE_DELIVERY_ADDRESS: "Choose a delivery address",
    ADD_NEW_ADDRESS: "Add new Address",
};

const AddressListContent = React.memo(function AddressListContent(
    props: AddressListContentProps & BottomSheetChildrenProps,
): React.ReactElement {
    useFocusTrap();
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);

    const selectedAddress = useMemo(
        () => (props?.selectedAddressIndex >= 0 ? props?.addresses[props?.selectedAddressIndex] : null),
        [props?.addresses, props?.selectedAddressIndex],
    );

    const handleAddNewAddressClick = useCallback(
        () =>
            props.addNewAddress({
                selectedAddress,
                addressList: props.addresses,
                source: ADDRESS_LIST_SOURCE.ADDRESS_LIST,
            }),
        [props, selectedAddress],
    );

    useEffect(() => {
        previouslyFocusedElement.current = getActiveElement();
    }, []);

    useEffect(() => {
        props?.onImpression(selectedAddress, props?.addresses);
    }, [props, selectedAddress]);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    return (
        <>
            <div className={styles.addressListHeader}>
                <div
                    className={styles.addressListHeaderTitle}
                    ref={headerDivRef as RefObject<HTMLDivElement>}
                    role="heading"
                    aria-level={3}
                    tabIndex={1}
                >
                    {TEXTS.CHOOSE_DELIVERY_ADDRESS}
                </div>
            </div>
            <div className={styles.addressListContent} data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST}>
                {props.addresses.map((address) => (
                    <Address
                        address={address}
                        selectAddress={props.selectAddress}
                        key={address.id}
                        addressList={props.addresses}
                    />
                ))}
                <button
                    onClick={handleAddNewAddressClick}
                    className={styles.addressAddNew}
                    aria-label={TEXTS.ADD_NEW_ADDRESS}
                    data-testid={SELECT_ADDRESS_TEST_IDS.SELECT_ADDRESS_LIST_ADD_NEW_ADDRESS}
                >
                    <AriaHidden as="div" className={styles.addressAddNewIcon} />
                    <AriaHidden as="div" className={styles.addressAddNewText}>
                        {TEXTS.ADD_NEW_ADDRESS}
                    </AriaHidden>
                    <ChevronRightIcon />
                </button>
            </div>
        </>
    );
});

function AddressList(props: AddressListProps): ReactElement {
    const { hideOnOverlayClick = true, onClose, ..._props } = props;
    const [actionOnClose] = useState<VoidFunction | undefined>();

    const _onClose = useCallback((): void => {
        actionOnClose && actionOnClose();
        onClose && onClose();
    }, [onClose, actionOnClose]);

    return (
        <BottomSheet
            hideOnOverlayClick={hideOnOverlayClick}
            onClose={_onClose}
            containerClass={styles.addressListBottomPosition}
        >
            {(bProps: BottomSheetChildrenProps): React.ReactElement => <AddressListContent {...bProps} {..._props} />}
        </BottomSheet>
    );
}

export default memo(AddressList);
